import React from "react";
/* eslint-disable react/prop-types */

/**
 * The avatar frame. One geometry, two sources.
 *
 * The source is ALWAYS cover-cropped around `focal` and never stretched — this is
 * the whole point of the component. EnrichedPlayer's SCSS uses `object-fit: fill`,
 * which squeezes a 16:9 presenter into a portrait strip and makes them gaunt; we
 * deliberately do not inherit that stylesheet.
 *
 * Recorder mode renders the POSTER even when a clip exists. A plain <video> under
 * the seek-and-screenshot loop leaves stale frames — the problem RecorderCanvasVideo
 * was written to solve — and RecorderCanvasVideo itself is H.264-only on an opaque
 * black canvas and fires onReady even when it fails, so a bad clip exports as a
 * silent black rectangle with no error. Until AvatarPlayer ships its own recorder
 * renderer (modelled on HotspotImages' RecorderCanvasImage: clearRect, no
 * backgroundColor), exporting the still is the correct, deterministic behaviour.
 */
export default function AvatarFrame({
  state = {},
  props = {},
  isEditor = false,
  isRecorder = false,
}) {
  const {
    posterUrl = "",
    videoUrl = null,
    jobStatus = "idle",
    focalX = 50,
    focalY = 50,
    radius = 16,
    avatarName = "",
  } = state;

  const objectPosition = `${focalX}% ${focalY}%`;
  const showClip = Boolean(videoUrl) && jobStatus === "done" && !isRecorder;

  const frameStyle = {
    position: "relative",
    width: "100%",
    height: "100%",
    overflow: "hidden",
    borderRadius: `${radius}px`,
  };

  const mediaStyle = {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    objectPosition,
    display: "block",
  };

  if (showClip) {
    return (
      <div className="avatar-player-frame" style={frameStyle}>
        <video
          className="avatar-player-media"
          src={videoUrl}
          poster={posterUrl || undefined}
          style={mediaStyle}
          autoPlay
          playsInline
          // The clip carries the narration, so it is NOT muted in playback.
          // If the flow ever emits silent clips, the narration must move to
          // soundList or the scene goes quiet with no error.
          preload="auto"
        />
      </div>
    );
  }

  if (!posterUrl) {
    return (
      <div className="avatar-player-frame avatar-player-empty" style={frameStyle}>
        <span className="avatar-player-empty-label">
          {avatarName || (isEditor ? "Avatar" : "")}
        </span>
      </div>
    );
  }

  return (
    <div className="avatar-player-frame" style={frameStyle}>
      <img
        className="avatar-player-media"
        src={posterUrl}
        alt={avatarName || "avatar"}
        style={mediaStyle}
        draggable={false}
      />
      {isEditor && jobStatus === "processing" ? <div className="avatar-player-badge">…</div> : null}
    </div>
  );
}
